/**
 * TWO-STEP SIGN-IN — an authenticator-app code on top of the password.
 *
 * The published Security & Trust page lists multi-factor authentication; until
 * 2026-09-14 there was none. It is available to every account and managed by
 * the account holder alone:
 *
 *   off     → "Set up" shows a QR code (and the key, for apps that cannot
 *             scan), and one code from the app proves it works;
 *   codes   → ten one-time recovery codes, shown ONCE, for a lost phone;
 *   on      → sign-in asks for a code after the password; recovery codes can
 *             be replaced; turning it off needs the password AND a code.
 *
 * Backend: src/auth/mfa.js, src/auth/totp.js, migration 103 (lobbero_be).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styled from '@emotion/styled';
import { apiRoute } from '../../../constants/api-constants';
import toast from '../../../utils/toast';
import { AppButton } from '../../button/app-button';
import { Field, Input, Label } from '../../onboarding/onboarding.styled';
import { SectionHeading, SectionLead } from './account.styled';
import {
  palette,
  radius,
  spacing,
  status as statusTone,
  typography,
} from '../../../style-utils/theme';
import { up } from '../../../style-utils/media-queries';

type Stage = 'loading' | 'off' | 'setup' | 'codes' | 'on';

interface MfaStatus {
  enabled: boolean;
  enabled_at: string | null;
  recovery_codes_left: number;
}

const serverMessage = (error: unknown, fallback: string): string =>
  (error as { response?: { data?: { message?: string } } }).response?.data
    ?.message || fallback;

const Block = styled.section`
  margin-top: ${spacing.lg};
  padding: ${spacing.md};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  background: ${palette.surface};
`;

const BlockTitle = styled.h3`
  margin: 0 0 ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const BlockText = styled.p`
  margin: 0 0 ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

const StatusLine = styled.p`
  margin: 0 0 ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${statusTone.success.fg};
`;

/* Phone first: the QR above the key; side by side from md up. */
const SetupGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing.md};
  align-items: start;

  ${up('md')} {
    grid-template-columns: auto 1fr;
  }
`;

const Qr = styled.img`
  width: 192px;
  max-width: 100%;
  height: auto;
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
`;

const KeyText = styled.code`
  display: block;
  margin: ${spacing.half} 0 ${spacing.sm};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.xs};
  background: ${palette.surfaceMuted};
  font-family: ${typography.mono};
  font-size: ${typography.size.sm}px;
  letter-spacing: 0.08em;
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

const CodeList = styled.ul`
  list-style: none;
  margin: 0 0 ${spacing.sm};
  padding: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing.xs};

  ${up('sm')} {
    grid-template-columns: 1fr 1fr;
  }

  li {
    padding: ${spacing.xs} ${spacing.sm};
    border-radius: ${radius.xs};
    background: ${palette.surfaceMuted};
    font-family: ${typography.mono};
    font-size: ${typography.size.sm}px;
    color: ${palette.ink};
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.sm};
`;

const Refusal = styled.p`
  margin: ${spacing.sm} 0 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${statusTone.alert.fg};
`;

const groupKey = (key: string) => key.replace(/(.{4})/g, '$1 ').trim();

export const AccountMfa: React.FC = () => {
  const [stage, setStage] = useState<Stage>('loading');
  const [info, setInfo] = useState<MfaStatus | null>(null);
  const [setup, setSetup] = useState<{ secret: string; qr: string } | null>(null);
  const [codes, setCodes] = useState<string[]>([]);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${apiRoute}/auth/mfa`);

      setInfo(data);
      setStage(data.enabled ? 'on' : 'off');
    } catch {
      setRefusal('We could not load your sign-in settings. Please reload the page.');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const act = async (fn: () => Promise<void>, fallback: string) => {
    if (busy) return;
    setBusy(true);
    setRefusal(null);
    try {
      await fn();
    } catch (error) {
      setRefusal(serverMessage(error, fallback));
    } finally {
      setBusy(false);
    }
  };

  const beginSetup = () =>
    act(async () => {
      const { data } = await axios.post(`${apiRoute}/auth/mfa/setup`, {});

      setSetup({ secret: data.secret, qr: data.qr_data_url });
      setCode('');
      setStage('setup');
    }, 'We could not start the set-up. Please try again.');

  const turnOn = () =>
    act(async () => {
      const { data } = await axios.post(`${apiRoute}/auth/mfa/enable`, {
        code: code.trim(),
        password,
      });

      setCodes(data.recovery_codes || []);
      setSetup(null);
      setCode('');
      setPassword('');
      setStage('codes');
    }, 'That code did not work. Try the newest code from your app.');

  const newCodes = () =>
    act(async () => {
      const { data } = await axios.post(`${apiRoute}/auth/mfa/recovery-codes`, { code: code.trim() });

      setCodes(data.recovery_codes || []);
      setCode('');
      setStage('codes');
    }, 'That code did not work.');

  const turnOff = () =>
    act(async () => {
      await axios.post(`${apiRoute}/auth/mfa/disable`, { password, code: code.trim() });
      setPassword('');
      setCode('');
      toast.success('Two-step sign-in is off.');
      await load();
    }, 'We could not turn it off.');

  const downloadCodes = () => {
    const text = `Lobbero recovery codes — each works once.\n\n${codes.join('\n')}\n`;
    const url = window.URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    const link = document.createElement('a');

    link.href = url;
    link.download = 'lobbero-recovery-codes.txt';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      <SectionHeading>Two-step sign-in</SectionHeading>
      <SectionLead>
        A code from an authenticator app on your phone, asked for after your
        password. Someone who learns your password still cannot get in.
      </SectionLead>

      {stage === 'off' ? (
        <Block>
          <BlockTitle>Two-step sign-in is off</BlockTitle>
          <BlockText>
            You will need an authenticator app — Google Authenticator, Microsoft
            Authenticator, 1Password or any app that shows 6-digit codes.
          </BlockText>
          <AppButton variant="primary" size="compact" loading={busy} loadingLabel="Starting" onClick={beginSetup}>
            Set up two-step sign-in
          </AppButton>
        </Block>
      ) : null}

      {stage === 'setup' && setup ? (
        <Block>
          <BlockTitle>Scan this with your authenticator app</BlockTitle>
          <SetupGrid>
            <Qr src={setup.qr} alt="QR code to add Lobbero to your authenticator app" />
            <div>
              <BlockText>Can’t scan it? Enter this key in the app instead:</BlockText>
              <KeyText id="mfa-secret">{groupKey(setup.secret)}</KeyText>
              <Field>
                <Label htmlFor="mfa-setup-code">Code from your app</Label>
                <Input
                  id="mfa-setup-code"
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                />
              </Field>
              <Field>
                <Label htmlFor="mfa-setup-password">Your password</Label>
                <Input
                  id="mfa-setup-password"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </Field>
              <BlockText>
                Turning it on signs out your other devices; sign in there again
                with a code.
              </BlockText>
              <AppButton
                variant="primary"
                size="compact"
                loading={busy}
                loadingLabel="Checking"
                disabled={code.trim().length < 6 || !password}
                onClick={turnOn}
              >
                Turn on
              </AppButton>
            </div>
          </SetupGrid>
        </Block>
      ) : null}

      {stage === 'codes' ? (
        <Block>
          <BlockTitle>Save your recovery codes</BlockTitle>
          <BlockText>
            If you lose your phone, each of these signs you in once instead of an
            app code. This is the only time they are shown — keep them somewhere
            safe, away from your phone.
          </BlockText>
          <CodeList data-testid="mfa-recovery-codes">
            {codes.map(c => (
              <li key={c}>{c}</li>
            ))}
          </CodeList>
          <Actions>
            <AppButton variant="secondary" size="compact" onClick={downloadCodes}>
              Download codes
            </AppButton>
            <AppButton variant="primary" size="compact" onClick={() => load()}>
              I’ve saved them
            </AppButton>
          </Actions>
        </Block>
      ) : null}

      {stage === 'on' && info ? (
        <Block>
          <StatusLine>Two-step sign-in is on</StatusLine>
          <BlockText>
            {info.recovery_codes_left} of 10 recovery codes left. To replace them
            or turn two-step sign-in off, enter a code from your app first.
          </BlockText>
          <Field>
            <Label htmlFor="mfa-manage-code">Code from your app</Label>
            <Input
              id="mfa-manage-code"
              value={code}
              onChange={e => setCode(e.target.value)}
              autoComplete="one-time-code"
              placeholder="123456"
            />
          </Field>
          <Actions>
            <AppButton variant="secondary" size="compact" disabled={code.trim().length < 6 || busy} onClick={newCodes}>
              New recovery codes
            </AppButton>
          </Actions>
          <Field style={{ marginTop: spacing.md }}>
            <Label htmlFor="mfa-off-password">Your password (to turn it off)</Label>
            <Input
              id="mfa-off-password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </Field>
          <AppButton
            variant="destructive"
            size="compact"
            disabled={!password || code.trim().length < 6 || busy}
            onClick={turnOff}
          >
            Turn off two-step sign-in
          </AppButton>
        </Block>
      ) : null}

      {refusal ? <Refusal role="alert">{refusal}</Refusal> : null}
    </>
  );
};
